import { useEffect, useState } from 'react';
import {
  inferProcedureInput,
  inferProcedureOutput,
  AnyProcedure,
} from '@trpc/server';
import { TRPCClientErrorLike } from '@trpc/client';
import { UseTRPCQueryResult } from '@trpc/react-query/shared';
import { GalleryViewMode } from '../components/gallery/types';

interface HasId {
  id: string;
}

interface UseFilteredGalleryPaginationProps<TQueryFn extends AnyProcedure> {
  queryFn: (
    input: inferProcedureInput<TQueryFn>,
    options?: Parameters<TQueryFn>[1],
  ) => UseTRPCQueryResult<
    inferProcedureOutput<TQueryFn>,
    TRPCClientErrorLike<TQueryFn>
  >;
  queryInput: Omit<inferProcedureInput<TQueryFn>, 'limit' | 'offset'>;
}

export function useFilteredGalleryPagination<TQueryFn extends AnyProcedure>({
  queryFn,
  queryInput,
}: UseFilteredGalleryPaginationProps<TQueryFn>) {
  const [data, setData] = useState<inferProcedureOutput<TQueryFn> | null>(null);
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);
  const [viewMode, setViewMode] = useState<GalleryViewMode>('list');
  const [previousInput, setPreviousInput] = useState(queryInput);

  const {
    data: rawData,
    error,
    isLoading,
    refetch: refetchRawData,
    isFetching,
  } = queryFn({
    ...queryInput,
    limit: pageSize,
    offset: page > 0 ? page * pageSize : 0,
  });

  const reset = () => {
    setData(null);
    setPage(0);
  };

  // Refetch data and reset state
  const refetch = () => {
    // don't reset to maintain the current page
    setData(null);
    refetchRawData();
  };

  // Reset data if the filter changes
  useEffect(() => {
    const isNewFilter =
      JSON.stringify(previousInput) !== JSON.stringify(queryInput);

    if (isNewFilter) {
      reset();
      setPreviousInput(queryInput);
    }
  }, [queryInput, previousInput]);

  // this useeffect handles the data processing based on the view mode
  // if we are in grid view we want to append data in a list
  // if we are in list view we want to replace the data
  useEffect(() => {
    if (!rawData || isLoading) return;

    if (!isFetching) {
      setData((prevData) => {
        if (viewMode === 'grid') {
          if (!prevData) {
            return rawData;
          }

          const newData: HasId[] = prevData.records;
          rawData.records.forEach((item: HasId) => {
            if (!newData.find((i) => i.id === item.id)) {
              newData.push(item);
            }
          });

          return {
            ...rawData,
            records: newData,
          };
        } else {
          return rawData;
        }
      });
    }
  }, [rawData, viewMode, isLoading, isFetching]);

  const onViewChange = (viewMode: GalleryViewMode) => {
    setViewMode(viewMode);
    reset();
  };

  const onPageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
    reset();
  };

  return {
    viewMode,
    setViewMode: onViewChange,
    data,
    setData,
    pageSize,
    setPageSize: onPageSizeChange,
    page,
    setPage,
    error,
    isLoading,
    refetch,
  };
}
